import { useCallback, useMemo } from 'react';
import { useAuth } from '@/hooks/use-auth';

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const RETRYABLE_STATUS = [502, 503, 504];

/**
 * Thin admin API wrapper around authedFetch. Every call is prefixed with
 * `/api/admin`, sends the bearer token, throws a friendly Error on non-2xx,
 * and parses JSON. GET requests silently retry once on network failures /
 * gateway errors.
 */
export const useAdminApi = () => {
  const { authedFetch } = useAuth();

  const request = useCallback(async (path, { method = 'GET', body } = {}) => {
    const attempt = () => authedFetch(`/api/admin${path}`, {
      method,
      cache: 'no-store',
      headers: body ? { 'Content-Type': 'application/json' } : {},
      body: body ? JSON.stringify(body) : undefined,
    });
    let res;
    try {
      res = await attempt();
    } catch (netErr) {
      if (method !== 'GET') throw new Error('Network error — check your connection and try again');
      await sleep(700);
      try { res = await attempt(); }
      catch { throw new Error('Network error — check your connection and try again'); }
    }
    if (!res.ok && method === 'GET' && RETRYABLE_STATUS.includes(res.status)) {
      await sleep(700);
      try { res = await attempt(); } catch { /* keep original response */ }
    }
    if (!res.ok) {
      let detail = `Request failed (${res.status})`;
      let payload = null;
      try {
        payload = await res.json();
        if (typeof payload?.detail === 'string') detail = payload.detail;
        else if (payload?.detail?.errors?.length) detail = `${payload.detail.errors.length} validation issue(s)`;
        else if (Array.isArray(payload?.detail)) detail = payload.detail.map((d) => d.msg || JSON.stringify(d)).join(' · ');
        else if (payload?.detail) detail = JSON.stringify(payload.detail);
      } catch { /* noop */ }
      const err = new Error(detail);
      err.status = res.status;
      err.data = payload;
      throw err;
    }
    if (res.status === 204) return null;
    return res.json();
  }, [authedFetch]);

  // Memoize so admin pages using `api` in useEffect deps don't re-fetch on
  // every parent re-render (see notes in use-api.js).
  return useMemo(() => ({
    get: (p) => request(p),
    post: (p, body) => request(p, { method: 'POST', body }),
    patch: (p, body) => request(p, { method: 'PATCH', body }),
    put: (p, body) => request(p, { method: 'PUT', body }),
    del: (p) => request(p, { method: 'DELETE' }),
    // Returns a Blob for binary/file downloads (e.g. CSV export). Callers
    // are responsible for turning it into a downloadable URL.
    blob: async (p) => {
      const res = await authedFetch(`/api/admin${p}`, { cache: 'no-store' });
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      return res.blob();
    },
    // POST that returns a Blob (e.g. generate-then-download a PDF from a payload).
    blobPost: async (p, body) => {
      const res = await authedFetch(`/api/admin${p}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      return res.blob();
    },
  }), [request, authedFetch]);
};

export default useAdminApi;
